import {Component, OnInit, ViewChild} from '@angular/core';
import {CookieService} from 'ngx-cookie-service';
import {isEmpty, routePath, utf8Decode} from 'sdv-common';
import {AlarmTypeService} from '../service/alarm-type.service';
import {ApiService} from '../service/api.service';
import {ProjectService} from '../service/project.service';
import {getRandomRgb, keys, redirect, reload, retryAfterMillis} from '../shared/constants';
import {AlarmService} from '../service/alarm.service';
import {BaseChartDirective} from 'ng2-charts';
import {Stat} from "../model/stat.model";

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit {

  settings = {
    alarmType: {
      system: true,
      info: true,
      warning: true,
      error: true,
      success: false,
      timing: false
    },
    interval: 60000,
    points: 60,
    top: 10
  };
  nextLoad = this.getNextLoad();
  loadTimeout = 0;
  detailColors = {};
  private firstLoad = true;
  private startMillis: number;
  @ViewChild('lineChart', {read: BaseChartDirective})
  private lineChart: BaseChartDirective;
  @ViewChild('doughnutChart', {read: BaseChartDirective})
  private doughnutChart: BaseChartDirective;

  public stats: Stat[] = [];

  public doughnutChartData = {
    labels: [],
    datasets: [{
      data: [],
      borderColor: [],
      backgroundColor: []
    }]
  };
  public doughnutChartOptions = {
    cutout: '70%',
    plugins: {
      legend: {
        display: true
      }
    }
  };

  public lineChartData = {
    datasets: [],
    labels: []
  };
  public lineChartOptions = {
    responsive: true,
    scales: {
      x: {
        display: true
      },
      y: {
        min: 0,
        display: true,
        beginAtZero: true,
        suggestedMax: 10,
        ticks: {
          stepSize: 1
        }
      }
    },
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      legend: {
        display: true
      },
      tooltip: {
        callbacks: {
          footer: (items) => {
            return 'Total: ' + items.map(n => n.parsed.y).reduce((a, b) => a + b, 0);
          }
        }
      }
    }
  };

  constructor(public apiService: ApiService,
              public alarmService: AlarmService,
              public alarmTypeService: AlarmTypeService,
              public projectService: ProjectService,
              public cookieService: CookieService) {
    const projectId = this.cookieService.get(keys.project);
    if (this.apiService.isAnonymous()) {
      redirect(routePath.login);
    } else if (isEmpty(projectId)) {
      redirect('/projects');
    } else {
      this.apiService.setParams({
        $project: projectId
      });
    }
  }

  ngOnInit() {
    const settings = this.cookieService.get('dashboard-settings');
    if (settings) {
      this.settings = JSON.parse(settings);
    }
    this.startFrom(new Date().getTime() - (this.settings.interval * this.settings.points));
    this.load();

    this.lineChartOptions.plugins.legend.display = this.notIndividualView();
    this.doughnutChartOptions.plugins.legend.display = this.notIndividualView();
    setInterval(() => this.nextLoad = this.getNextLoad(), 1000);
  }

  getNextLoad() {
    return (new Date().getSeconds() * 100) / 60;
    //todo this.settings.interval fix progress bar for more then 1min range
  }

  startFrom(millis) {
    const startDate = new Date(millis);
    startDate.setMilliseconds(0);
    startDate.setSeconds(0);
    this.startMillis = startDate.getTime();
  }

  load() {
    clearTimeout(this.loadTimeout);

    const typeIds = [];
    if (this.settings.alarmType.system) {
      typeIds.push(1);
    }
    if (this.settings.alarmType.info) {
      typeIds.push(2);
    }
    if (this.settings.alarmType.warning) {
      typeIds.push(3);
    }
    if (this.settings.alarmType.error) {
      typeIds.push(4);
    }
    if (this.settings.alarmType.success) {
      typeIds.push(5);
    }
    if (this.settings.alarmType.timing) {
      typeIds.push(6);
    }

    const params = {
      interval: this.settings.interval,
      points: this.settings.points,
      types: typeIds.join(','),
      start: this.startMillis,
      top: this.settings.top
    };
    const alarmId = this.apiService.getParam('$alarm');
    if (!isEmpty(alarmId)) {
      Object.assign(params, {alarms: alarmId});
    }
    this.apiService.get('stats', params).subscribe(response => {
      for (const value of (response.values || [])) {
        const stat = this.convertStat(value);
        const index = this.stats.findIndex(n => n.alarmId === stat.alarmId);
        if (index >= 0) {
          this.stats[index].points.push(...stat.points);
          this.stats[index].points.shift();
        } else {
          this.stats.push(stat);
        }
        const alarm = this.alarmService.get(stat.alarmId);

        if (this.doughnutChartData.labels.indexOf(alarm.code) === -1) {
          this.doughnutChartData.labels.push(alarm.code);
        }

        let dataset;
        for (const ds of this.lineChartData.datasets) {
          if (ds.label === alarm.code) {
            dataset = ds;
            break;
          }
        }
        if (!dataset) {
          const color = this.alarmTypeService.getColorByTypeId(alarm.type);
          dataset = {
            backgroundColor: this.rgb(color),
            borderColor: this.rgb(color),
            pointStyle: 'circle',
            label: alarm.code,
            pointRadius: 6,
            tension: 0.5, // smooth
            fill: false,
            data: []
          };
          this.lineChartData.datasets.push(dataset);
        }
        // in case when it's not first load and something new happened (need to feel previous points as 0)
        while (dataset.data.length + stat.points.length < this.settings.points) {
          dataset.data.push(0);
        }
        dataset.data.push(...stat.points);
      }

      const removeDatasetIndexes = [];
      for (let i = 0; i < this.lineChartData.datasets.length; i++) {
        const dataset = this.lineChartData.datasets[i];
        if (!this.firstLoad) {
          dataset.data.shift();
        }
        while (dataset.data.length < this.settings.points) {
          dataset.data.push(0);
        }
        if (dataset.data.reduce((a, b) => a + b, 0) === 0) {
          removeDatasetIndexes.push(i);
        }
      }
      // remove from last to first to not broke indexes
      removeDatasetIndexes.reverse();
      for (const index of removeDatasetIndexes) {
        this.lineChartData.datasets.splice(index, 1);
      }

      let time = response.start;
      for (let i = 0; i < (this.firstLoad ? this.settings.points : 1); i++) {
        this.lineChartData.labels.push(new Date(time).toLocaleString().trim());
        time += response.interval;
      }
      this.startMillis = time;

      while (this.lineChartData.labels.length > this.settings.points) {
        this.lineChartData.labels.shift();
      }

      this.doughnutChartData.datasets[0].data = [];
      this.doughnutChartData.datasets[0].borderColor = [];
      this.doughnutChartData.datasets[0].backgroundColor = [];
      for (const dataset of this.lineChartData.datasets) {
        this.doughnutChartData.datasets[0].borderColor.push(dataset.borderColor);
        this.doughnutChartData.datasets[0].backgroundColor.push(dataset.backgroundColor);
        this.doughnutChartData.datasets[0].data.push(dataset.data.reduce((sum, a) => sum + a, 0));
      }

      if (this.doughnutChart) {
        this.doughnutChart.chart.update();
      }
      if (this.lineChart) {
        this.lineChart.chart.update();
      }

      this.firstLoad = false;
      let delay = this.settings.interval;
      if (new Date().getSeconds() > 0) {
        delay -= new Date().getSeconds() * 1000;
      }
      this.loadTimeout = setTimeout(() => this.load(), delay);
    }, err => {
      this.apiService.error({}, err);
      this.loadTimeout = setTimeout(() => this.load(), retryAfterMillis);
    });
  }

  private convertStat(stat) {
    return {
      points: stat.points || [],
      alarmId: utf8Decode(stat.alarmId || ''),
      details: (stat.details || []).map(n => {
        return {
          id: utf8Decode(n.id || ''),
          cause: utf8Decode(n.cause || ''),
          happened: n.happened || 0,
          ip: utf8Decode(n.ip || ''),
          source: utf8Decode(n.source || ''),
          count: n.count || 0,
          attributes: Object.keys(n.attributes || {}).map(a => {
            return {
              key: a,
              value: n.attributes[a]
            };
          }),
          stacks: n.stacks || []
        };
      })
    };
  }

  chartClicked(e) {
    console.log(e);
  }

  getProjectName() {
    return this.projectService.getName(this.cookieService.get(keys.project));
  }

  notIndividualView() {
    return this.apiService.getParam('$alarm') === null;
  }

  rgb(color, alpha = 1) {
    return 'rgba(' + color.red + ', ' + color.green + ', ' + color.blue + ', ' + alpha + ')';
  }

  saveSettings() {
    this.cookieService.set('dashboard-settings', JSON.stringify(this.settings));
    this.detailColors = {};
    this.stats = [];
    this.load();

    const alarm = this.apiService.getParam('$alarm');
    if (isEmpty(alarm)) {
      reload('/dashboard');
    } else {
      reload('/dashboard?$alarm=' + alarm);
    }
  }

  getStats() {
    const results = [];
    for (const stat of this.stats) {
      let index = results.findIndex(n => n.alarmId === stat.alarmId);
      if (index === -1) {
        index = results.push({
          alarmId: stat.alarmId,
          unique: [],
          count: 0
        }) - 1;
      }
      results[index].lastOccurred = Math.max(...stat.details.map(n => n.happened));
      results[index].count += stat.points.reduce((sum, a) => sum + a, 0);
      let stackId = '';
      for (const detail of stat.details) {
        detail.stacks.sort(n => n.index);
        for (const stack of detail.stacks) {
          stackId += stack.id;
        }
      }
      if (!isEmpty(stackId)) {
        results[index].unique.push(stackId);
      }
    }
    for (const result of results) {
      result.unique = Array.from(new Set(result.unique));
    }
    return results;
  }

  setShow(stat) {
    this.lineChartData.datasets.forEach(n => {
      if (n.label === this.alarmService.get(stat.alarmId).code) {
        stat.hidden = !stat.hidden;
        n.hidden = !n.hidden;
      }
    });
    if (this.lineChart) {
      this.lineChart.chart.update();
    }
  }

  getDetailColor(detail) {
    let stackId = '';
    detail.stacks.sort(n => n.index);
    for (const stack of detail.stacks) {
      stackId += stack.id;
    }
    let color = this.detailColors[stackId];
    if (!color) {
      color = getRandomRgb();
      this.detailColors[stackId] = color;
    }
    return this.rgb(color, 0.3);
  }
}
